import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import { LegalEntityDetailsDocument } from 'generated/resources/graphql'
import { SetLegalEntityWorkplaceDocument } from 'generated/resources/graphql'

import Form from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
// nosemgrep:do_not_use_panes
import PanesHeader from 'modules/core/components/PanesHeader'
// nosemgrep:do_not_use_panes [as no replacement yet, coming soon and will be updated in bulk]
import PanesPadForm from 'modules/core/components/PanesPadForm'
import SelectField from 'modules/core/components/SelectField'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { useCreateLocation } from 'modules/locations/hooks/useCreateLocation'

interface Location {
  id: number
  name?: string
  city?: string | null
  postalCode?: string | null
  country?: string | null
}

interface WorkplaceData {
  current?: Location | null
  availableOptions: Location[]
}

interface Props {
  legalEntity: LegalEntity
  workplaceData: WorkplaceData | null
}

const UNSET_VALUE = '__unset__'

const WorkplaceBlock: React.FC<Props> = ({ legalEntity, workplaceData }: Props) => {
  const [selectedValue, setSelectedValue] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [setWorkplace] = useMutation(SetLegalEntityWorkplaceDocument, {
    extractorFn: (data) => data.locationsMutations?.setLocationsLegalEntityWorkplace,
  })

  const { createLocation } = useCreateLocation()

  React.useEffect(() => {
    if (workplaceData?.current?.id) {
      setSelectedValue(String(workplaceData.current.id))
    } else {
      setSelectedValue(UNSET_VALUE)
    }
  }, [workplaceData])

  if (!workplaceData) {
    return null
  }

  const { availableOptions, current } = workplaceData
  const currentId = current?.id ?? null

  const findRecommendedLocation = (): Location | null => {
    const lePostalCode = legalEntity.attributes.postal_code?.toLowerCase().trim()
    const leCity = legalEntity.attributes.city?.toLowerCase().trim()

    if (!lePostalCode || !leCity) {
      return null
    }

    return (
      availableOptions.find((loc) => {
        const locPostalCode = loc.postalCode?.toLowerCase().trim()
        const locCity = loc.city?.toLowerCase().trim()
        return locPostalCode === lePostalCode && locCity === leCity
      }) || null
    )
  }

  const recommendedLocation = findRecommendedLocation()

  const options: Array<{ label: string; value: string }> = [
    { label: `-- ${i18n.t('common.select')} --`, value: UNSET_VALUE },
  ]

  const isValidDisplayValue = (value: string | null | undefined): boolean => {
    if (!value) return false
    // if there is an invalid city, the translation may be missing
    if (value.includes('Translation missing:')) return false
    if (value.match(/^[a-z_]+\.[a-z_]+/i)) return false
    return true
  }

  availableOptions.forEach((loc) => {
    const isRecommended = recommendedLocation?.id === loc.id
    const hasValidCity = isValidDisplayValue(loc.city)
    const locationLabel = hasValidCity ? `${loc.name ?? ''} (${loc.city})` : (loc.name ?? '')
    const label = isRecommended ? `>> ${locationLabel}` : locationLabel

    options.push({
      label,
      value: String(loc.id),
    })
  })

  const handleChange = async (value: string) => {
    if (value === UNSET_VALUE && !currentId) {
      return
    }

    setSelectedValue(value)
    setIsSubmitting(true)

    const locationId = value === UNSET_VALUE ? null : Number(value)

    try {
      const result = await setWorkplace({
        variables: {
          companyId: legalEntity.attributes.company_id,
          legalEntityId: legalEntity.id,
          locationId,
        },
        refetchQueries: [
          {
            query: LegalEntityDetailsDocument,
            variables: {
              companyId: legalEntity.attributes.company_id,
              legalEntityId: legalEntity.id,
            },
          },
        ],
      })

      handleResult(result, () => {
        setSelectedValue(currentId ? String(currentId) : null)
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCreateLocation = async () => {
    setIsSubmitting(true)

    try {
      const result = await createLocation({
        name: legalEntity.attributes.legal_name,
        country: legalEntity.attributes.country || '',
        city: legalEntity.attributes.city || undefined,
        state: legalEntity.attributes.state || undefined,
        postalCode: legalEntity.attributes.postal_code || undefined,
        addressLineOne: legalEntity.attributes.address_line_1 || undefined,
        addressLineTwo: legalEntity.attributes.address_line_2 || undefined,
        companyId: legalEntity.attributes.company_id,
        timezone: 'Etc/UTC', // Default timezone, will be updated by user
        main: false,
      })

      const handled = handleResult(result, () => {})
      if (handled.success && handled.data.location?.id) {
        navigate(
          navigation.settings.locations.locationsId.details.fullPath({
            id: handled.data.location.id,
          })
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PanesPadForm direction="horizontal">
      <PanesHeader
        illustration="server-edit"
        title={i18n.t('settings.locations.title')}
        description={i18n.t('job_catalog.migration.map_legal_entity_to_workplace_description')}
      />

      <Form withBackground>
        <FormRow>
          <SelectField
            label={i18n.t('settings.locations.add.form.name')}
            value={selectedValue || undefined}
            onChange={(value) => handleChange(value)}
            options={options}
          />
        </FormRow>

        <FormRow footer>
          <Button
            onClick={handleCreateLocation}
            disabled={isSubmitting || selectedValue !== UNSET_VALUE}
            iconName="add"
          >
            {i18n.t('settings.locations.action')}
          </Button>
        </FormRow>
      </Form>
    </PanesPadForm>
  )
}

export default WorkplaceBlock
